import { tm } from '../tm'
import type { CSSProperties, ReactNode } from 'react'

const BUTTON_VARIANT = {
  base: 'bg-transparent transition-buttonBadge scale-1 hover:scale-95 hover:bg-light-beige focus:bg-light-beige border border-transparent focus-visible:scale-95 transform-gpu ease-out duration-300',
  disabled:
    'text-gray bg-white border-light-gray border-opacity-30 cursor-not-allowed scale-unset hover:bg-white hover:scale-1 focus-visible:scale-1 focus:bg-white',
}

const SIZES = {
  small: 'px-2 py-1 text-xs',
  normal: 'py-2 px-4 text-xs',
  medium: 'py-2 px-4 text-sm',
  big: 'py-3 px-5 text-sm',
  huge: 'py-4 px-5 text-base',
}

const VARIANTS = {
  'white-with-border': {
    base: 'bg-white !border-inside',
    selected: 'bg-light-gray text-black',
  },
  gray: {
    base: '',
    selected: 'bg-light-gray text-black border-none',
  },
  'gray-static': {
    base: 'bg-light-gray',
    selected: 'bg-medium-beige text-black border-none',
  },
  outline: {
    base: 'bg-white border border-gray',
    selected:
      'bg-black !outline-black text-white border border-white hover:bg-black/80 hover:border-white focus:bg-black/80 focus:border-white',
  },
  purple: {
    base: 'bg-medium-purple',
    selected:
      'bg-medium-purple border-medium-purple hover:bg-light-purple hover:border-light-purple focus:bg-light-purple focus:border-light-purple',
  },
  green: {
    base: 'bg-medium-green',
    selected:
      'bg-medium-green border-medium-green hover:bg-light-green hover:border-light-green focus:bg-light-green focus:border-light-green',
  },
  orange: {
    base: 'bg-medium-orange',
    selected:
      'bg-medium-orange border-medium-orange hover:bg-light-orange hover:border-light-orange focus:bg-light-orange focus:border-light-orange',
  },
  blue: {
    base: 'bg-medium-blue',
    selected:
      'bg-medium-blue border-medium-blue hover:bg-light-blue hover:border-light-blue focus:bg-light-blue focus:border-light-blue',
  },
  red: {
    base: 'bg-medium-red',
    selected:
      'bg-medium-red border-medium-red hover:bg-light-red hover:border-light-red focus:bg-light-red focus:border-light-red',
  },
  beige: {
    base: 'bg-medium-beige',
    selected:
      'bg-medium-beige border-medium-beige hover:bg-light-beige hover:border-light-beige focus:bg-light-beige focus:border-light-beige',
  },
  'light-beige': {
    base: 'bg-light-beige',
    selected:
      'bg-light-beige border-light-beige hover:bg-medium-beige hover:border-medium-beige focus:bg-medium-beige focus:border-medium-beige',
  },
  'light-blue': {
    base: 'bg-light-blue',
    selected:
      'bg-light-blue border-light-blue hover:bg-medium-blue hover:border-medium-blue focus:bg-medium-blue focus:border-medium-blue',
  },
  // TODO: delete once API switch
  pink: {
    base: 'bg-medium-red',
    selected:
      'bg-medium-red border-medium-red hover:bg-light-red hover:border-light-red focus:bg-light-red focus:border-light-red',
  },
  'gg-blue': {
    base: 'bg-gg-light-blue hover:bg-gg-medium-blue text-dark-gray',
    selected:
      'bg-gg-blue border-gg-blue text-white hover:bg-gg-medium-blue focus:bg-gg-medium-blue ',
  },
}

export type BadgeVariant = keyof typeof VARIANTS
export type BadgeSize = keyof typeof SIZES

export interface BadgeProps<Value> {
  children: ReactNode
  className?: string
  onClick?: (value: Value | undefined) => void
  selected?: boolean
  style?: CSSProperties
  disabled?: boolean
  value?: Value
  variant: BadgeVariant
  size?: BadgeSize
}

const emptyObject = {}
export function Badge<Value>({
  children,
  className = '',
  onClick = undefined,
  selected = false,
  style = emptyObject,
  value = undefined,
  disabled = false,
  variant,
  size = 'normal',
}: BadgeProps<Value>) {
  if (!children) return null

  const isButton = !!onClick
  const Component = isButton ? 'button' : 'span'

  const handleOnClick = () => {
    onClick?.(value)
  }

  return (
    <Component
      type={isButton ? 'button' : undefined}
      onClick={isButton && !disabled ? handleOnClick : undefined}
      className={tm(
        'block w-fit truncate rounded-full font-semibold text-black',
        SIZES[size],
        VARIANTS[variant].base,
        isButton && BUTTON_VARIANT.base,
        isButton && selected && VARIANTS[variant].selected,
        isButton && disabled && BUTTON_VARIANT.disabled,
        className,
      )}
      style={style}
      disabled={disabled}
    >
      {children}
    </Component>
  )
}
